import Phaser from 'phaser';
import type { AnimalKey, CharacterConfig } from '../types';
import { ANIMAL_STATS } from '../config/animals';
import { makeAnimal } from './animals';
import { makeRider } from './character';

// Animals that have an AI-generated sprite-sheet animation loaded by BootScene.
// Others fall back to the procedural drawing in makeAnimal.
interface SpriteCfg {
  animKey: string;
  firstFrameKey: string;
  scale: number;
  offsetY: number;
  flipX?: boolean;
  fakeRun?: boolean;
  // Where on the sprite (in source pixels from sprite center, before scale)
  // the rider's seat should land. Positive y is down. Used to override the
  // animal's saddleAnchor when an AI sprite is in use.
  riderSaddleY: number;
  riderSaddleX: number;
}

const SPRITE_ANIMALS: Partial<Record<AnimalKey, SpriteCfg>> = {
  horse: {
    animKey: 'horse_run',
    firstFrameKey: 'horse_run_0',
    scale: 0.26,
    offsetY: 38,
    flipX: true,
    riderSaddleY: -110,
    riderSaddleX: -10,
  },
  pig: {
    animKey: 'pig_run',
    firstFrameKey: 'pig_run_0',
    scale: 0.06,
    offsetY: 38,
    flipX: false,
    riderSaddleY: -200,
    riderSaddleX: -180,
  },
  sheep: {
    animKey: 'sheep_run',
    firstFrameKey: 'sheep_run_0',
    scale: 0.07,
    offsetY: 42,
    flipX: true,
    fakeRun: true,
    riderSaddleY: -260,
    riderSaddleX: 80,
  },
  donkey: {
    animKey: 'donkey_run',
    firstFrameKey: 'donkey_run_0',
    scale: 0.07,
    offsetY: 42,
    flipX: false,
    fakeRun: true,
    riderSaddleY: -180,
    riderSaddleX: 0,
  },
  duck: {
    animKey: 'duck_run',
    firstFrameKey: 'duck_run_0',
    scale: 0.07,
    offsetY: 42,
    flipX: true,
    fakeRun: true,
    riderSaddleY: -200,
    riderSaddleX: 200,
  },
  otter: {
    animKey: 'otter_run',
    firstFrameKey: 'otter_run_0',
    scale: 0.07,
    offsetY: 42,
    flipX: true,
    fakeRun: true,
    riderSaddleY: -180,
    riderSaddleX: 0,
  },
  clownfish: {
    animKey: 'clownfish_run',
    firstFrameKey: 'clownfish_run_0',
    scale: 0.07,
    offsetY: 38,
    flipX: false,
    fakeRun: true,
    riderSaddleY: -180,
    riderSaddleX: 0,
  },
  stingray: {
    animKey: 'stingray_run',
    firstFrameKey: 'stingray_run_0',
    scale: 0.07,
    offsetY: 30,
    flipX: false,
    fakeRun: true,
    riderSaddleY: -150,
    riderSaddleX: 0,
  },
  turtle: {
    animKey: 'turtle_run',
    firstFrameKey: 'turtle_run_0',
    scale: 0.07,
    offsetY: 38,
    flipX: true,
    fakeRun: true,
    riderSaddleY: -180,
    riderSaddleX: 0,
  },
  seal: {
    animKey: 'seal_run',
    firstFrameKey: 'seal_run_0',
    scale: 0.07,
    offsetY: 42,
    flipX: false,
    fakeRun: true,
    riderSaddleY: -180,
    riderSaddleX: 0,
  },
};

export function makeMountWithRider(
  scene: Phaser.Scene,
  animalKey: AnimalKey,
  character: CharacterConfig,
  x: number,
  y: number,
  options: { animate?: boolean; showRider?: boolean } = {},
): Phaser.GameObjects.Container {
  const animate = options.animate ?? true;
  const showRider = options.showRider ?? true;
  const wrapper = scene.add.container(x, y);

  const spriteCfg = SPRITE_ANIMALS[animalKey];
  if (spriteCfg && scene.textures.exists(spriteCfg.firstFrameKey)) {
    const sprite = scene.add.sprite(0, spriteCfg.offsetY, spriteCfg.firstFrameKey);
    sprite.setScale(spriteCfg.scale);
    if (spriteCfg.flipX) sprite.setFlipX(true);
    if (animate && scene.anims.exists(spriteCfg.animKey)) {
      sprite.play(spriteCfg.animKey);
    }
    if (animate && spriteCfg.fakeRun) {
      // Fake a run cycle for static sprites: small bob + slight rotation.
      const baseY = spriteCfg.offsetY;
      const baseScale = spriteCfg.scale;
      scene.tweens.add({
        targets: sprite,
        y: baseY - 8,
        duration: 180,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      scene.tweens.add({
        targets: sprite,
        angle: { from: -3, to: 3 },
        duration: 240,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      scene.tweens.add({
        targets: sprite,
        scaleY: baseScale * 0.96,
        duration: 180,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
    wrapper.add(sprite);
  } else {
    const animal = makeAnimal(scene, animalKey, 0, 0);
    wrapper.add(animal);
  }

  if (showRider) {
    const rider = makeRider(scene, character);
    const stats = ANIMAL_STATS[animalKey];
    if (spriteCfg && scene.textures.exists(spriteCfg.firstFrameKey)) {
      const flipSign = spriteCfg.flipX ? -1 : 1;
      rider.x = spriteCfg.riderSaddleX * spriteCfg.scale * flipSign;
      rider.y = spriteCfg.offsetY + spriteCfg.riderSaddleY * spriteCfg.scale;
    } else {
      rider.x = stats.saddleAnchor.x;
      rider.y = stats.saddleAnchor.y;
    }
    wrapper.add(rider);
  }
  return wrapper;
}
